import { Switch } from '@/components/ui/Switch';
import type { TeamMember } from '../api/team.api';
import type { MemberAccountAccess } from '../member-account-access';

/** A canEdit role (owner/admin/sub_admin) holds full access by default. */
function hasFullAccess(role: TeamMember['role']): boolean {
  return role !== 'member';
}

/**
 * A canEdit role (owner/admin) shows static "Full Access"; a plain member gets
 * the interactive toggle - unless the viewer is read-only (invite-only, no
 * team.manage) or the member's account is not active (suspended/deactivated/
 * deleted - MYN-133/135), either of which renders the same visible state as a
 * disabled switch (like `InvitedToggle`), matching the Figma pending-row
 * treatment rather than inventing a new "can't touch this" visual. The
 * account-status case additionally bakes the reason into the switch's
 * accessible label, the same way the pending-invite toggle already does.
 * Promoting a member to admin flips their columns to Full Access
 * automatically (admin holds both by role default).
 */
export function PermissionCell({
  member,
  enabled,
  onToggle,
  label,
  readOnly = false,
  accountAccess,
}: {
  member: TeamMember;
  enabled: boolean;
  onToggle: (next: boolean) => void;
  label: string;
  readOnly?: boolean;
  accountAccess: MemberAccountAccess;
}) {
  if (hasFullAccess(member.role)) {
    return <span className="whitespace-nowrap text-sm font-medium text-dkgreen">Full Access</span>;
  }
  if (readOnly || !accountAccess.isActive) {
    const disabledLabel = accountAccess.isActive
      ? label
      : `${label} (${accountAccess.disabledReason})`;
    return (
      <div className="flex items-center gap-2">
        <Switch checked={enabled} disabled label={disabledLabel} />
        <span className="text-sm text-slate">{enabled ? 'Enabled' : 'Disabled'}</span>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2">
      <Switch checked={enabled} onCheckedChange={onToggle} label={label} />
      <span className="text-sm text-slate">{enabled ? 'Enabled' : 'Disabled'}</span>
    </div>
  );
}

/**
 * Disabled toggle for a pending invite — permissions can't be set until join.
 * Shows "Disabled" (not a bare "-") to match both the Figma pending-row
 * treatment and our own toggle convention (PermissionCell's Enabled/Disabled).
 */
export function InvitedToggle({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-2">
      <Switch checked={false} disabled label={label} />
      <span className="text-sm text-slate">Disabled</span>
    </div>
  );
}
